import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NavigationEnd, Router, RouterModule, RouterOutlet } from '@angular/router';
import { filter, firstValueFrom, Subject, Subscription, takeUntil } from 'rxjs';

import { LoginService } from '../auth/login.service';
import { AssignmentDownPaymentService } from '../assignments/assignment-down-payment.service';
import { MENU_STRUCTURE, MenuItem } from '../shared/constants/api.constants';
import { isCrmPushConfigured, syncCrmFcmPush } from '../shared/push/crm-push';
import { PermissionUtils } from '../shared/utils/permission.utils';
import { NotificationHubService } from '../settings/notification-hub.service';
import { BreadcrumbDataService } from './breadcrumb-data.service';
import { FooterComponent } from './footer/footer.component';
import { HeaderComponent } from './header/header.component';
import { RecentPagesService } from './recent-pages/recent-pages.service';
import { SidebarComponent } from './sidebar/sidebar.component';
import { WENTY_SUPPORT_TICKET_PROMPT, WentyComponent } from '../wenty/wenty.component';
import { BirthdayCelebrationComponent } from '../shared/birthday-celebration/birthday-celebration.component';
import { shouldShowBirthdayCelebration } from '../shared/birthday-celebration/birthday-celebration.utils';

interface BreadcrumbItem {
  label: string;
  route: string;
  isHome?: boolean;
}

const UNAUTH_LAYOUT_PATHS = [
  '/login',
  '/forgot-password',
  '/reset-password',
  '/oauth/connect',
] as const;

const isUnauthLayoutPath = (url: string): boolean =>
  (UNAUTH_LAYOUT_PATHS as readonly string[]).includes(url.split('?')[0]);

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    RouterModule,
    MatSidenavModule,
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    MatListModule,
    MatMenuModule,
    MatTooltipModule,
    MatBadgeModule,
    HeaderComponent,
    SidebarComponent,
    FooterComponent,
    WentyComponent,
    BirthdayCelebrationComponent,
  ],
  templateUrl: './layout.component.html',
  styleUrl: './layout.component.scss',
})
export class LayoutComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);

  // Signals para estado reactivo
  isSidenavExpanded = signal(false);
  currentRoute = signal(this.router.url);
  isHandset = signal(false);
  /** Birthday overlay — FE-only, once per year (see birthday-celebration.utils). */
  showBirthdayCelebration = signal(false);

  // Allows detail components (contact/client/talent) to inject the
  // loaded entity name as the breadcrumb leaf. The `breadcrumbs` computed
  // below reacts to its `overridesSignal` so the breadcrumb updates as
  // soon as the data arrives.
  private readonly breadcrumbData = inject(BreadcrumbDataService);
  private readonly recentPages = inject(RecentPagesService);
  private readonly downPaymentService = inject(AssignmentDownPaymentService);
  private readonly notificationHub = inject(NotificationHubService);
  /** Once per layout lifetime (each full page load / login shell). */
  private pushSyncStarted = false;

  // Computed signals
  sidenavWidth = computed(() => (this.isSidenavExpanded() ? 280 : 72));
  currentPageTitle = computed(() => this.getPageTitleFromRoute(this.currentRoute()));
  breadcrumbs = computed(() =>
    this.generateBreadcrumbs(
      this.currentRoute(),
      this.breadcrumbData.overridesSignal(),
      this.breadcrumbData.countsSignal()
    )
  );
  showFooter = computed(() => {
    const isLoggedIn = this.loginService.isAuthenticated();
    const currentUrl = this.currentRoute();
    return isLoggedIn && !isUnauthLayoutPath(currentUrl);
  });

  private destroy$ = new Subject<void>();
  private authSub: Subscription;
  private userSub: Subscription;
  private routeSub!: Subscription;

  showHeader = false;
  showSidebar = false;
  showWenty = false; // Wenty FAB: logged-in + 2FA + `wenty:use`
  has2FAEnabled = false; // Track 2FA status to pass to header
  @ViewChild(WentyComponent) private wenty?: WentyComponent;

  constructor(
    private breakpointObserver: BreakpointObserver,
    private loginService: LoginService,
    public permissionUtils: PermissionUtils
  ) {
    // Effect para observar cambios de breakpoint
    effect(() => {
      this.breakpointObserver
        .observe(Breakpoints.Handset)
        .pipe(takeUntil(this.destroy$))
        .subscribe(result => {
          this.isHandset.set(result.matches);
          if (result.matches) {
            this.isSidenavExpanded.set(false);
          }
        });
    });

    // Effect para observar cambios de ruta
    effect(() => {
      this.router.events
        .pipe(
          filter(event => event instanceof NavigationEnd),
          takeUntil(this.destroy$)
        )
        .subscribe((event: NavigationEnd) => {
          this.currentRoute.set(event.url);
          this.updateLayoutVisibility(this.loginService.isAuthenticated(), event.url);
        });
    });

    // Persist last ~50 visited pages (URL + breadcrumb) for menu search.
    // Re-runs when leaf labels load asynchronously after NavigationEnd.
    effect(() => {
      const route = this.currentRoute();
      const crumbs = this.breadcrumbs();
      if (!this.loginService.isAuthenticated()) {
        return;
      }
      this.recentPages.record(
        route,
        crumbs.map(c => c.label)
      );
    });

    // Auth state subscription
    this.authSub = this.loginService.isLoggedIn$().subscribe(isLoggedIn => {
      this.updateLayoutVisibility(isLoggedIn, this.router.url);
      if (!isLoggedIn) {
        this.pushSyncStarted = false;
        return;
      }
      if (!isUnauthLayoutPath(this.router.url)) {
        this.maybeSyncPushSilent();
      }
    });

    // User state subscription (to detect 2FA status changes)
    this.userSub = this.loginService.getCurrentUser$().subscribe(_user => {
      this.recentPages.reloadForCurrentUser();
      // Update layout visibility whenever user data changes (including 2FA status)
      if (this.loginService.isAuthenticated()) {
        this.updateLayoutVisibility(true, this.router.url);
        this.maybeShowBirthdayCelebration();
      } else {
        this.showBirthdayCelebration.set(false);
        this.updateLayoutVisibility(false, this.router.url);
      }
    });

    // Initial check - but only if user data is already available
    // Otherwise, wait for user data to load in ngOnInit
    const isAuthenticated = this.loginService.isAuthenticated();

    if (isAuthenticated) {
      const user = this.loginService.getUser();
      if (user) {
        // User data is already available, check now
        this.updateLayoutVisibility(true, this.router.url);
      }
      // If user data is not available, wait for ngOnInit to load it
    } else {
      // Not authenticated, hide sidebar
      this.updateLayoutVisibility(false, this.router.url);
    }
  }

  updateLayoutVisibility(isLoggedIn: boolean, url: string) {
    const isUnauth = isUnauthLayoutPath(url);

    // If not logged in or on unauth routes, hide sidebar and header
    if (!isLoggedIn || isUnauth) {
      this.showHeader = false;
      this.showSidebar = false;
      this.showWenty = false;
      this.has2FAEnabled = false;
      return;
    }

    // Check if user has 2FA enabled
    const user = this.loginService.getUser();

    // If user data is not available yet, hide sidebar but show header (without search)
    if (!user) {
      this.showHeader = true; // Show header even without user data
      this.showSidebar = false;
      this.showWenty = false;
      this.has2FAEnabled = false; // No search until user data loads
      return;
    }

    const has2FAEnabled = user.totpEnabled === true;
    this.has2FAEnabled = has2FAEnabled; // Store for header component

    // Show sidebar only if 2FA is enabled
    // Show header always (but search bar will be hidden if 2FA is not enabled)
    const shouldShowSidebar = has2FAEnabled;
    const shouldShowHeader = true; // Always show header, but search will be conditionally hidden

    // Always update to ensure change detection
    this.showHeader = shouldShowHeader;
    this.showSidebar = shouldShowSidebar;
    // Wenty rides the same gate as the sidebar (full logged-in user) and its
    // own `wenty:use` permission. UI no-render when the permission is missing.
    this.showWenty = shouldShowSidebar && this.permissionUtils.canUseWenty();

    if (shouldShowSidebar && this.permissionUtils.canApplyDownPayment()) {
      this.downPaymentService.refreshPendingCount();
    }
  }

  ngOnInit() {
    // Establecer ruta inicial
    this.currentRoute.set(this.router.url);

    const onUnauthRoute = isUnauthLayoutPath(this.router.url);

    // On login/forgot/reset, drop a stale cached profile so we never bounce to
    // /dashboard without a live HttpOnly cookie (profile cache ≠ cookie validity).
    if (onUnauthRoute) {
      if (this.loginService.isAuthenticated() && !this.loginService.hasUsableSession()) {
        this.loginService.clearToken();
      }
      this.updateLayoutVisibility(false, this.router.url);
      return;
    }

    // Initialize user data if authenticated
    if (this.loginService.isAuthenticated()) {
      // Use cached user if available, otherwise fetch
      const cachedUser = this.loginService.getUser();
      if (cachedUser) {
        this.updateLayoutVisibility(true, this.router.url);
        this.maybeSyncPushSilent();
      } else {
        this.loginService.fetchCurrentUser().subscribe({
          next: _user => {
            // User data loaded, update layout visibility
            this.updateLayoutVisibility(true, this.router.url);
            this.maybeSyncPushSilent();
          },
          error: error => {
            console.error('[Layout] Error fetching user data', error);
            // Error loading user, hide sidebar
            this.updateLayoutVisibility(false, this.router.url);
          },
        });
      }
    } else {
      // Not authenticated, ensure sidebar is hidden
      this.updateLayoutVisibility(false, this.router.url);
    }
  }

  /**
   * On every authenticated visit: request Notification permission if still
   * `default`, then refresh FCM token + upsert CrmPushSubscription. Silent
   * (no toasts) — Profile button still surfaces success/denied UX.
   */
  private maybeSyncPushSilent(): void {
    if (this.pushSyncStarted || !isCrmPushConfigured()) return;
    this.pushSyncStarted = true;
    void (async () => {
      try {
        const status = await firstValueFrom(this.notificationHub.getPushStatus());
        if (!status.enabled) return;
        await syncCrmFcmPush(token =>
          firstValueFrom(
            this.notificationHub.subscribePush({
              token,
              platform: 'web',
              userAgent: navigator.userAgent,
            })
          )
        );
      } catch {
        // Best-effort — layout must not break on push/FCM failures.
      }
    })();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.authSub) this.authSub.unsubscribe();
    if (this.userSub) this.userSub.unsubscribe();
    if (this.routeSub) this.routeSub.unsubscribe();
  }

  onSidenavMouseEnter() {
    if (!this.isHandset()) {
      this.isSidenavExpanded.set(true);
    }
  }

  onSidenavMouseLeave() {
    if (!this.isHandset()) {
      this.isSidenavExpanded.set(false);
    }
  }

  toggleSidenav() {
    return;
  }

  onMenuItemSelected(_item: string) {
    return;
  }

  openSupportViaWenty(): void {
    this.wenty?.openWithUserMessage(WENTY_SUPPORT_TICKET_PROMPT);
  }

  onBreadcrumbClick(route: string) {
    this.router.navigate([route]);
  }

  private getPageTitleFromRoute(route: string): string {
    const routeMap: Record<string, string> = {
      '/dashboard': 'Dashboard',
      '/clients': 'Gestión de Clientes',
      '/clients/new': 'Nuevo Cliente',
      '/clients/edit': 'Editar Cliente',
      '/projects': 'Proyectos',
      '/projects/new': 'Nuevo Proyecto',
      '/talents': 'Team Members',
      '/team-members': 'Team Members',
      '/talent/hr': 'Talent HR',
      '/lead-generators': 'Lead Generators',
      '/recruiters': 'Reclutadores',
      '/contracts': 'Contratos',
      '/milestones': 'Milestones',
      '/timesheets': 'Timesheets',
      '/finance/overview': 'Finance Overview',
      '/finance/invoices': 'Invoices',
      '/finance/payments': 'Payments',
      '/finance/commissions': 'Commissions',
      '/finance/bonus-claims': 'Bonus payment',
      '/reports': 'Reportes',
      '/settings': 'Configuración',
      '/relay/organizations': 'Organizations',
      '/relay/platform-ai': 'Platform AI',
      '/signals/watches': 'Watches',
      '/signals/runs': 'Runs',
      '/signals/spend': 'Spend',
      '/signals/providers': 'Providers',
      '/users': 'Usuarios',
      '/api-keys': 'API Keys',
    };

    return routeMap[route] || 'Dashboard';
  }

  private generateBreadcrumbs(
    route: string,
    overrides: Map<string, string> = new Map(),
    counts: Map<string, number> = new Map()
  ): BreadcrumbItem[] {
    const normalizedRoute = route.split('?')[0];

    const baseMatch = this.getMenuBaseBreadcrumbs(normalizedRoute);
    const baseBreadcrumbs = baseMatch.breadcrumbs;

    let result: BreadcrumbItem[];
    if (baseMatch.matchedRoute && baseBreadcrumbs.length > 0) {
      const extras = this.buildExtraBreadcrumbs(normalizedRoute, baseMatch.matchedRoute, overrides);
      result = [...baseBreadcrumbs, ...extras];
    } else {
      const fallback = this.buildFallbackBreadcrumbs(normalizedRoute, overrides);
      result = fallback.length > 0 ? fallback : [{ label: 'Dashboard', route: '/dashboard' }];
    }

    return this.appendLeafCount(result, normalizedRoute, counts);
  }

  /**
   * Appends the registered record count to the breadcrumb leaf so list pages
   * render `Invoices (128)` (Figma C3). Counts are only registered by the
   * list component whose route is currently active, so the leaf is always the
   * matching page title.
   */
  private appendLeafCount(
    breadcrumbs: BreadcrumbItem[],
    route: string,
    counts: Map<string, number>
  ): BreadcrumbItem[] {
    const count = counts.get(route) ?? counts.get(this.decodePath(route));
    if (count === undefined || breadcrumbs.length === 0) {
      return breadcrumbs;
    }
    const leaf = breadcrumbs[breadcrumbs.length - 1];
    return [...breadcrumbs.slice(0, -1), { ...leaf, label: `${leaf.label} (${count})` }];
  }

  private getMenuBaseBreadcrumbs(route: string): {
    breadcrumbs: BreadcrumbItem[];
    matchedRoute: string | null;
  } {
    let bestMatch: { section: MenuItem; child?: MenuItem; route: string } | null = null;

    for (const section of MENU_STRUCTURE) {
      if (section.route && this.isRoutePrefixMatch(route, section.route)) {
        if (!bestMatch || section.route.length > bestMatch.route.length) {
          bestMatch = { section, route: section.route };
        }
      }

      for (const child of section.children || []) {
        if (!child.route || child.disabled) {
          continue;
        }

        if (this.isRoutePrefixMatch(route, child.route)) {
          if (
            !bestMatch ||
            child.route.length > bestMatch.route.length ||
            (child.route.length === bestMatch.route.length && !bestMatch.child)
          ) {
            bestMatch = { section, child, route: child.route };
          }
        }
      }
    }

    if (!bestMatch) {
      return { breadcrumbs: [], matchedRoute: null };
    }

    const resolvedMatch = bestMatch;

    const breadcrumbs: BreadcrumbItem[] = [];

    if (resolvedMatch.child) {
      // There is no Talent landing page. On HR ops, both crumbs go to the HR
      // landing so "Talent" is not a 404 / Recruiter hop (KAN-275). Freeze the
      // trail at Talent > HR ops — tab segments must not become "Hr" (KAN-281).
      const isTalentHr = resolvedMatch.child.key === 'talent-hr';
      const talentHrLanding = '/talent/hr/org-chart';
      // Integrations apps use in-page tabs. Tab landings stay Integrations > App;
      // deeper routes still append after the app root.
      const isIntegrationApp =
        resolvedMatch.child.key === 'relay' || resolvedMatch.child.key === 'signals';
      const integrationParts = route.split('/').filter(Boolean);
      const integrationTabLanding = isIntegrationApp && integrationParts.length <= 2;
      breadcrumbs.push({
        label: resolvedMatch.section.label,
        route: isTalentHr
          ? talentHrLanding
          : resolvedMatch.section.route || resolvedMatch.child.route || resolvedMatch.route,
      });
      breadcrumbs.push({
        label: resolvedMatch.child.label,
        route: isTalentHr ? talentHrLanding : resolvedMatch.child.route!,
      });
      const matchedRoute = isTalentHr
        ? route
        : integrationTabLanding
          ? route
          : isIntegrationApp
            ? `/${integrationParts[0]}`
            : resolvedMatch.route;
      return {
        breadcrumbs,
        matchedRoute,
      };
    }

    breadcrumbs.push({ label: resolvedMatch.section.label, route: resolvedMatch.route });
    return { breadcrumbs, matchedRoute: resolvedMatch.route };
  }

  private buildExtraBreadcrumbs(
    currentRoute: string,
    matchedRoute: string,
    overrides: Map<string, string>
  ): BreadcrumbItem[] {
    if (!currentRoute.startsWith(matchedRoute)) {
      return [];
    }

    const suffix = currentRoute.slice(matchedRoute.length).replace(/^\//, '');
    if (!suffix) {
      return [];
    }

    const segments = suffix.split('/').filter(Boolean);
    const extras: BreadcrumbItem[] = [];
    let currentPath = matchedRoute;

    segments.forEach(segment => {
      currentPath = `${currentPath}/${segment}`;

      if (segment === 'list') {
        return;
      }

      extras.push({
        label: this.resolveSegmentLabel(segment, currentPath, overrides),
        route: currentPath,
      });
    });

    return extras;
  }

  private buildFallbackBreadcrumbs(
    route: string,
    overrides: Map<string, string>
  ): BreadcrumbItem[] {
    return route
      .split('/')
      .filter(Boolean)
      .map((segment, index, all) => {
        const segmentRoute = `/${all.slice(0, index + 1).join('/')}`;
        return {
          label: this.resolveSegmentLabel(segment, segmentRoute, overrides),
          route: segmentRoute,
        };
      });
  }

  /**
   * Resolves the display label for a breadcrumb segment.
   * Order of precedence:
   * 1. Explicit override registered by a detail component for the full URL.
   *    The lookup is tolerant to URL encoding differences (`:` vs `%3A`).
   * 2. Static label lookup for known segments.
   * 3. Generic "Detalle" fallback for numeric ids, plain UUIDs, and
   *    compound ids of the form `CATEGORY:uuid` (used by the unified
   *    contacts model, e.g. `OTHER:fe195caf-...`).
   * 4. Capitalised slug derived from kebab-case segment.
   */
  private resolveSegmentLabel(
    segment: string,
    fullPath: string,
    overrides: Map<string, string>
  ): string {
    const overrideLabel = overrides.get(fullPath) ?? overrides.get(this.decodePath(fullPath));
    if (overrideLabel) {
      return overrideLabel;
    }
    return this.mapDynamicSegmentLabel(segment);
  }

  /**
   * `decodeURIComponent` mirror of the BreadcrumbDataService normalizer,
   * tolerant to malformed escape sequences so a single bad character
   * cannot blow up the whole layout.
   */
  private decodePath(path: string): string {
    try {
      return decodeURIComponent(path);
    } catch {
      return path;
    }
  }

  private mapDynamicSegmentLabel(segment: string): string {
    const routeLabelMap: Record<string, string> = {
      new: 'New',
      edit: 'Edit',
      details: 'Detail',
      detail: 'Detail',
      profile: 'Profile',
      processes: 'Processes',
      'talents-process': 'Talent Process',
      technologies: 'Technologies',
      specialities: 'Specialities',
      assignments: 'Assignments',
      contacts: 'Contacts',
      gifts: 'Gifts',
      address: 'Address',
      settings: 'Settings',
      roles: 'Roles & Permissions',
      list: 'List',
      hr: 'HR ops',
      'cv-preview': 'CV Preview',
      organizations: 'Organizations',
      members: 'Members',
      workspaces: 'Workspaces',
      relay: 'Relay',
      signals: 'Signals',
      watches: 'Watches',
      runs: 'Runs',
      spend: 'Spend',
      providers: 'Providers',
    };

    if (routeLabelMap[segment]) {
      return routeLabelMap[segment];
    }

    if (
      /^\d+$/.test(segment) ||
      /^[0-9a-fA-F-]{8,}$/.test(segment) ||
      /^[A-Z][A-Z_]*:.+$/.test(segment)
    ) {
      return 'Detail';
    }

    return segment
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  private isRoutePrefixMatch(currentRoute: string, targetRoute: string): boolean {
    return currentRoute === targetRoute || currentRoute.startsWith(`${targetRoute}/`);
  }

  onBirthdayCelebrationDismissed(): void {
    this.showBirthdayCelebration.set(false);
  }

  /** CRM FE-only: once on birthday (local calendar), independent of gifts notify. */
  private maybeShowBirthdayCelebration(): void {
    if (isUnauthLayoutPath(this.router.url)) return;
    const user = this.loginService.getUser();
    if (
      !shouldShowBirthdayCelebration({
        userId: user?.id,
        birthday: user?.contact?.birthday ?? user?.birthday,
      })
    ) {
      return;
    }
    this.showBirthdayCelebration.set(true);
  }
}
